import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Gantt, Willow } from '@svar-ui/react-gantt';
import '@svar-ui/react-gantt/style.css';
import type { ITask, ILink, IScaleConfig, IApi } from '@svar-ui/react-gantt';
import { getProjectRequirements, RequirementWithDetails, updateRequirement } from '../services/requirementService';
import { getProjectDependencies, createDependency, deleteDependency, getCriticalPath, recalculateSchedule, RequirementDependency } from '../services/requirementDependencyService';
import Layout from '../components/Layout';

type ViewMode = 'Day' | 'Week' | 'Month' | 'Year';

const formatQuarter = (date: Date) => `Q${Math.floor(date.getMonth() / 3) + 1}`;

const scaleConfigs: Record<ViewMode, IScaleConfig[]> = {
  Day: [
    { unit: 'month', step: 1, format: '%F %Y' },
    { unit: 'day', step: 1, format: '%j' },
  ],
  Week: [
    { unit: 'month', step: 1, format: '%F %Y' },
    { unit: 'week', step: 1, format: 'Week %W' },
  ],
  Month: [
    { unit: 'year', step: 1, format: '%Y' },
    { unit: 'month', step: 1, format: '%F' },
  ],
  Year: [
    { unit: 'year', step: 1, format: '%Y' },
    { unit: 'quarter', step: 1, format: formatQuarter },
  ],
};

const columns = [
  { id: 'text', header: 'Task Name', flexgrow: 1 },
  {
    id: 'start',
    header: 'Start',
    align: 'center' as const,
    width: 110,
    template: (v: Date) => {
      if (!v) return '';
      const m = String(v.getMonth() + 1).padStart(2, '0');
      const d = String(v.getDate()).padStart(2, '0');
      return `${m}-${d}-${v.getFullYear()}`;
    },
  },
  { id: 'duration', header: 'Days', align: 'center' as const, width: 70 },
];

const ASSIGNEE_COLORS = [
  { bg: '#4f46e5', light: 'rgba(79, 70, 229, 0.18)', label: 'Indigo' },
  { bg: '#0891b2', light: 'rgba(8, 145, 178, 0.18)', label: 'Cyan' },
  { bg: '#059669', light: 'rgba(5, 150, 105, 0.18)', label: 'Emerald' },
  { bg: '#d97706', light: 'rgba(217, 119, 6, 0.18)', label: 'Amber' },
  { bg: '#dc2626', light: 'rgba(220, 38, 38, 0.18)', label: 'Red' },
  { bg: '#7c3aed', light: 'rgba(124, 58, 237, 0.18)', label: 'Violet' },
  { bg: '#db2777', light: 'rgba(219, 39, 119, 0.18)', label: 'Pink' },
  { bg: '#2563eb', light: 'rgba(37, 99, 235, 0.18)', label: 'Blue' },
  { bg: '#65a30d', light: 'rgba(101, 163, 13, 0.18)', label: 'Lime' },
  { bg: '#ea580c', light: 'rgba(234, 88, 12, 0.18)', label: 'Orange' },
];

const DEPENDENCY_TYPE_MAP: Record<string, 'e2s' | 's2s' | 'e2e' | 's2e'> = {
  'finish-to-start': 'e2s',
  'start-to-start':  's2s',
  'finish-to-finish': 'e2e',
  'start-to-finish':  's2e',
};

const LINK_TYPE_TO_DB: Record<string, string> = {
  'e2s': 'finish-to-start',
  's2s': 'start-to-start',
  'e2e': 'finish-to-finish',
  's2e': 'start-to-finish',
};

function calculateProgress(status: string): number {
  switch (status) {
    case 'completed': return 100;
    case 'in-progress': return 50;
    case 'approved': return 25;
    default: return 0;
  }
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

const Timeline2: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const apiRef = useRef<IApi | null>(null);

  const [requirements, setRequirements] = useState<RequirementWithDetails[]>([]);
  const [projectDependencies, setProjectDependencies] = useState<RequirementDependency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('Month');
  const [schedulingId, setSchedulingId] = useState<string | null>(null);
  const [unscheduledExpanded, setUnscheduledExpanded] = useState(true);
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [editingTask, setEditingTask] = useState<RequirementWithDetails | null>(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingDepId, setDeletingDepId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [criticalPathIds, setCriticalPathIds] = useState<Set<string>>(new Set());
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showCriticalPath, setShowCriticalPath] = useState(false);

  // Refs for stable access in init callback
  const requirementsRef = useRef<RequirementWithDetails[]>([]);
  const projectIdRef = useRef(projectId);
  const tokenRef = useRef(token);
  const projectDependenciesRef = useRef<RequirementDependency[]>([]);
  const linkIdToDepIdRef = useRef<Map<number, string>>(new Map());

  useEffect(() => { requirementsRef.current = requirements; }, [requirements]);
  useEffect(() => { projectIdRef.current = projectId; }, [projectId]);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { projectDependenciesRef.current = projectDependencies; }, [projectDependencies]);

  // Fetch requirements and dependencies
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId || !token) return;
      try {
        setLoading(true);
        setError(null);

        const [reqResult, depResult, cpResult] = await Promise.allSettled([
          getProjectRequirements(projectId, token),
          getProjectDependencies(projectId, token),
          getCriticalPath(projectId, token),
        ]);

        if (reqResult.status === 'fulfilled') {
          setRequirements(reqResult.value);
        } else {
          setError(reqResult.reason?.message || 'Failed to load requirements');
        }

        if (depResult.status === 'fulfilled') {
          setProjectDependencies(depResult.value);
        }

        if (cpResult.status === 'fulfilled') {
          setCriticalPathIds(new Set(cpResult.value));
        }
        // Silently ignore dependency/critical-path fetch failures
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId, token]);

  // Split into scheduled / unscheduled
  const scheduledReqs = useMemo(
    () => requirements.filter(r => r.expected_start_date && r.due_date),
    [requirements]
  );
  const unscheduledReqs = useMemo(() => {
    const base = requirements.filter(r => !r.expected_start_date || !r.due_date);
    if (assigneeFilter === 'all') return base;
    if (assigneeFilter === 'unassigned') return base.filter(r => !r.assigned_to_name);
    return base.filter(r => r.assigned_to_name === assigneeFilter);
  }, [requirements, assigneeFilter]);

  // Apply assignee filter to scheduled reqs
  const filteredScheduledReqs = useMemo(() => {
    if (assigneeFilter === 'all') return scheduledReqs;
    if (assigneeFilter === 'unassigned') return scheduledReqs.filter(r => !r.assigned_to_name);
    return scheduledReqs.filter(r => r.assigned_to_name === assigneeFilter);
  }, [scheduledReqs, assigneeFilter]);

  // Build assignee → color index map
  const assigneeColorMap = useMemo(() => {
    const names = new Set<string>();
    requirements.forEach(r => {
      if (r.assigned_to_name) names.add(r.assigned_to_name);
    });
    const map = new Map<string, number>();
    let idx = 0;
    Array.from(names).sort().forEach(name => {
      map.set(name, idx % ASSIGNEE_COLORS.length);
      idx++;
    });
    return map;
  }, [requirements]);

  // Build SVAR tasks (filtered scheduled only)
  const tasks: ITask[] = useMemo(() => {
    const ids = new Set(filteredScheduledReqs.map(r => r.id));
    const parentIdsWithChildren = new Set(
      filteredScheduledReqs
        .filter(r => r.parent_requirement_id && ids.has(r.parent_requirement_id))
        .map(r => r.parent_requirement_id!)
    );

    const items = filteredScheduledReqs.map(req => {
      const start = new Date(req.expected_start_date!);
      const end = new Date(req.due_date!);
      const duration = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
      const hasParent = req.parent_requirement_id && ids.has(req.parent_requirement_id);
      const isSummary = req.hierarchy_type === 'parent' && parentIdsWithChildren.has(req.id);

      const task: ITask = {
        id: req.id,
        text: req.title,
        start,
        end,
        duration,
        progress: calculateProgress(req.status),
        type: isSummary ? 'summary' : 'task',
      };

      if (isSummary) task.open = true;
      if (hasParent) task.parent = req.parent_requirement_id!;

      return task;
    });
    // Spacer row so the last real bar isn't flush against the bottom edge
    if (items.length > 0) {
      items.push({ id: '_spacer', text: '', start: new Date(), end: new Date(), duration: 0, progress: 0, type: 'task' });
    }
    return items;
  }, [filteredScheduledReqs, assigneeColorMap]);

  // Build SVAR links from project dependencies
  const links: ILink[] = useMemo(() => {
    const ids = new Set(filteredScheduledReqs.map(r => r.id));
    const idMap = new Map<number, string>();
    const result = projectDependencies
      .filter(dep => ids.has(dep.requirement_id) && ids.has(dep.depends_on_requirement_id))
      .map((dep, i) => {
        const linkId = i + 1;
        idMap.set(linkId, dep.id);
        return {
          id: linkId,
          source: dep.depends_on_requirement_id,
          target: dep.requirement_id,
          type: DEPENDENCY_TYPE_MAP[dep.dependency_type] || 'e2s',
        };
      });
    linkIdToDepIdRef.current = idMap;
    return result;
  }, [filteredScheduledReqs, projectDependencies]);

  // Build sets for dependency highlight CSS
  const { selectedLinkIds, criticalPathLinkIds } = useMemo(() => {
    const selectedLinks = new Set<number>();
    const cpLinks = new Set<number>();
    const cpSet = criticalPathIds;

    links.forEach(link => {
      // Links connected to the selected task
      if (selectedTaskId && (link.source === selectedTaskId || link.target === selectedTaskId)) {
        selectedLinks.add(link.id as number);
      }
      // Links on the critical path (both endpoints must be on the path)
      if (cpSet.has(link.source as string) && cpSet.has(link.target as string)) {
        cpLinks.add(link.id as number);
      }
    });

    return { selectedLinkIds: selectedLinks, criticalPathLinkIds: cpLinks };
  }, [links, selectedTaskId, criticalPathIds]);

  // Handle task update from drag/resize
  const handleUpdateTask = useCallback(async (ev: any) => {
    if (ev.inProgress) return;

    const { id: taskId, task: taskUpdate } = ev;
    const pid = projectIdRef.current;
    const tok = tokenRef.current;
    if (!pid || !tok) return;

    const req = requirementsRef.current.find(r => r.id === taskId);
    if (!req) return;

    const updateData: Record<string, string> = {};
    if (taskUpdate.start) updateData.expected_start_date = taskUpdate.start.toISOString().split('T')[0];
    if (taskUpdate.end) updateData.due_date = taskUpdate.end.toISOString().split('T')[0];
    if (Object.keys(updateData).length === 0) return;

    try {
      await updateRequirement(pid, taskId, updateData, tok);
      setRequirements(prev =>
        prev.map(r => r.id === taskId ? { ...r, ...updateData } : r)
      );
    } catch (err: any) {
      setError(`Failed to update "${req.title}": ${err.message}`);
      setTimeout(() => setError(null), 5000);
      return;
    }

    // Recalculate dependent dates (non-blocking — date save already succeeded)
    try {
      const result = await recalculateSchedule(pid, taskId, tok);
      if (result?.shifts && result.shifts.length > 0) {
        const freshReqs = await getProjectRequirements(pid, tok);
        setRequirements(freshReqs);
        setToast({
          type: 'success',
          message: `${result.shifts.length} dependent task${result.shifts.length > 1 ? 's' : ''} shifted`,
        });
        setTimeout(() => setToast(null), 4000);
      }
    } catch {
      // Recalculation failed but date save succeeded — don't alarm the user
      console.warn('Dependency recalculation failed after date update');
    }
  }, []);

  // Schedule an unscheduled requirement (assigns today + 7 days)
  const handleSchedule = useCallback(async (reqId: string) => {
    if (!projectId || !token) return;
    setSchedulingId(reqId);

    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 7);

    const updateData = {
      expected_start_date: formatDate(today),
      due_date: formatDate(endDate),
    };

    try {
      await updateRequirement(projectId, reqId, updateData, token);
      setRequirements(prev =>
        prev.map(r => r.id === reqId ? { ...r, ...updateData } : r)
      );
    } catch (err: any) {
      setError(`Failed to schedule: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSchedulingId(null);
    }
  }, [projectId, token]);

  // Handle click on a task bar — open edit modal and track selection
  const handleSelectTask = useCallback((ev: any) => {
    const taskId = ev.id;
    setSelectedTaskId(taskId);
    const req = requirementsRef.current.find(r => r.id === taskId);
    if (!req) return;
    setEditingTask(req);
    setEditStartDate(req.expected_start_date ? req.expected_start_date.split('T')[0] : '');
    setEditDueDate(req.due_date ? req.due_date.split('T')[0] : '');
  }, []);

  // Refresh dependencies from API
  const refreshDependencies = useCallback(async () => {
    const pid = projectIdRef.current;
    const tok = tokenRef.current;
    if (!pid || !tok) return;
    try {
      const deps = await getProjectDependencies(pid, tok);
      setProjectDependencies(deps);
    } catch {
      // Silently ignore — existing links remain
    }
  }, []);

  // Dependencies for the currently editing task
  const editingTaskDeps = useMemo(() => {
    if (!editingTask) return [];
    const reqMap = new Map(requirements.map(r => [r.id, r]));
    return projectDependencies
      .filter(dep => dep.requirement_id === editingTask.id || dep.depends_on_requirement_id === editingTask.id)
      .map(dep => {
        const isPredecessor = dep.depends_on_requirement_id !== editingTask.id;
        const otherId = isPredecessor ? dep.depends_on_requirement_id : dep.requirement_id;
        const other = reqMap.get(otherId);
        return {
          depId: dep.id,
          requirementId: dep.requirement_id,
          otherTitle: other?.title || 'Unknown',
          otherCode: other?.requirement_code || '',
          direction: isPredecessor ? 'Predecessor' : 'Successor',
          type: dep.dependency_type,
        };
      });
  }, [editingTask, projectDependencies, requirements]);

  // Delete a dependency from the modal
  const handleDeleteDepFromModal = useCallback(async (depId: string, requirementId: string) => {
    const pid = projectIdRef.current;
    const tok = tokenRef.current;
    if (!pid || !tok) return;

    setDeletingDepId(depId);
    try {
      await deleteDependency(pid, requirementId, depId, tok);
      await refreshDependencies();
      setToast({ type: 'success', message: 'Dependency removed' });
      setTimeout(() => setToast(null), 3000);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to delete dependency' });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setDeletingDepId(null);
    }
  }, [refreshDependencies]);

  // Save edited dates from modal
  const handleSaveEdit = useCallback(async () => {
    if (!editingTask || !projectId || !token) return;
    if (!editStartDate || !editDueDate) return;

    setSaving(true);
    const updateData = {
      expected_start_date: editStartDate,
      due_date: editDueDate,
    };

    try {
      await updateRequirement(projectId, editingTask.id, updateData, token);
      setRequirements(prev =>
        prev.map(r => r.id === editingTask.id ? { ...r, ...updateData } : r)
      );
      setEditingTask(null);
    } catch (err: any) {
      setError(`Failed to update "${editingTask.title}": ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  }, [editingTask, editStartDate, editDueDate, projectId, token]);

  // Handle link creation from Gantt drag
  const handleAddLink = useCallback(async (ev: any) => {
    const { link } = ev;
    const pid = projectIdRef.current;
    const tok = tokenRef.current;
    if (!pid || !tok || !link) return false;

    try {
      await createDependency(pid, link.target as string, {
        depends_on_requirement_id: link.source as string,
        dependency_type: (LINK_TYPE_TO_DB[link.type] || 'finish-to-start') as any,
        lag_days: link.lag || 0,
      }, tok);
      await refreshDependencies();
      setToast({ type: 'success', message: 'Dependency created' });
      setTimeout(() => setToast(null), 3000);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to create dependency' });
      setTimeout(() => setToast(null), 5000);
      return false; // Cancel the link in SVAR
    }
  }, [refreshDependencies]);

  // Handle link deletion from Gantt
  const handleDeleteLink = useCallback(async (ev: any) => {
    const pid = projectIdRef.current;
    const tok = tokenRef.current;
    if (!pid || !tok) return false;

    const depId = linkIdToDepIdRef.current.get(ev.id);
    if (!depId) return;

    const dep = projectDependenciesRef.current.find(d => d.id === depId);
    if (!dep) return;

    try {
      await deleteDependency(pid, dep.requirement_id, depId, tok);
      await refreshDependencies();
      setToast({ type: 'success', message: 'Dependency removed' });
      setTimeout(() => setToast(null), 3000);
    } catch (err: any) {
      setToast({ type: 'error', message: 'Failed to delete dependency' });
      setTimeout(() => setToast(null), 5000);
      return false;
    }
  }, [refreshDependencies]);

  // SVAR init — called once on mount
  const handleInit = useCallback((api: IApi) => {
    apiRef.current = api;
    api.on('update-task', handleUpdateTask);
    api.on('select-task', handleSelectTask);
    api.intercept('add-link', handleAddLink);
    api.intercept('delete-link', handleDeleteLink);
  }, [handleUpdateTask, handleSelectTask, handleAddLink, handleDeleteLink]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full min-h-[400px]">
          <div className="text-gray-500 dark:text-gray-400">Loading timeline...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6 mb-6">
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            className="text-sm text-blue-100 hover:text-white mb-2 inline-flex items-center transition-colors"
          >
            &larr; Back to Project
          </button>
          <h1 className="text-3xl font-bold text-white">Timeline</h1>
          <p className="text-blue-100 mt-1">Interactive Gantt chart &mdash; drag bars to reschedule</p>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {toast && (
          <div className={`mb-4 p-3 rounded-md text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
              : 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
          }`}>
            {toast.message}
          </div>
        )}

        {/* View mode switcher */}
        <div className="flex items-center gap-2 mb-4">
          {(['Day', 'Week', 'Month', 'Year'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                viewMode === mode
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {mode}
            </button>
          ))}

          {/* Assignee filter */}
          <select
            value={assigneeFilter}
            onChange={e => setAssigneeFilter(e.target.value)}
            className="ml-4 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Assignees</option>
            {Array.from(assigneeColorMap.keys()).map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
            {scheduledReqs.some(r => !r.assigned_to_name) && (
              <option value="unassigned">Unassigned</option>
            )}
          </select>

          {criticalPathIds.size > 0 && (
            <button
              onClick={() => setShowCriticalPath(!showCriticalPath)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                showCriticalPath
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-1 ring-red-300 dark:ring-red-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Critical Path
            </button>
          )}

          <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
            {filteredScheduledReqs.length} of {requirements.length} scheduled
          </span>
        </div>

        {/* Assignee Legend */}
        {assigneeColorMap.size > 0 && (
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assignee:</span>
            {Array.from(assigneeColorMap.entries()).map(([name, idx]) => (
              <div key={name} className="flex items-center gap-1.5">
                <span
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: ASSIGNEE_COLORS[idx].bg }}
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">{name}</span>
              </div>
            ))}
            {scheduledReqs.some(r => !r.assigned_to_name) && (
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm flex-shrink-0 bg-blue-500" />
                <span className="text-xs text-gray-700 dark:text-gray-300">Unassigned</span>
              </div>
            )}
          </div>
        )}

        {/* Link type legend */}
        {links.length > 0 && (
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Links:</span>
            {[
              { label: 'Finish-to-Start', short: 'FS' },
              { label: 'Start-to-Start', short: 'SS' },
              { label: 'Finish-to-Finish', short: 'FF' },
              { label: 'Start-to-Finish', short: 'SF' },
            ].map(t => (
              <span key={t.short} className="text-xs text-gray-600 dark:text-gray-400">
                <span className="font-semibold">{t.short}</span> {t.label}
              </span>
            ))}
            {showCriticalPath && (
              <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                Red = Critical Path
              </span>
            )}
          </div>
        )}

        {/* Gantt Chart */}
        {tasks.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow flex items-center justify-center py-20">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No scheduled requirements</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Schedule requirements from the panel below to see them on the timeline.
              </p>
            </div>
          </div>
        ) : (
          <div
            className="svar-gantt-wrapper bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            style={{ height: Math.max(200, tasks.length * 38 + 72 + 10) }}
          >
            <Willow>
              <Gantt
                tasks={tasks}
                links={links}
                scales={scaleConfigs[viewMode]}
                columns={columns}
                cellHeight={38}
                scaleHeight={36}
                highlightTime={(date: Date, unit: string) => {
                  if (unit === 'day') {
                    const today = new Date();
                    if (date.getFullYear() === today.getFullYear() &&
                        date.getMonth() === today.getMonth() &&
                        date.getDate() === today.getDate()) {
                      return 'today-highlight';
                    }
                  }
                  return '';
                }}
                init={handleInit}
              />
            </Willow>
          </div>
        )}

        {/* Unscheduled Requirements Panel */}
        {unscheduledReqs.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mt-4">
            <button
              onClick={() => setUnscheduledExpanded(!unscheduledExpanded)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
            >
              <div className="flex items-center gap-2">
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${unscheduledExpanded ? 'rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Unscheduled Requirements
                </h3>
                <span className="px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-full">
                  {unscheduledReqs.length}
                </span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Click "Schedule" to add to timeline
              </span>
            </button>

            {unscheduledExpanded && (
              <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
                {unscheduledReqs.map(req => (
                  <div
                    key={req.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750"
                  >
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {req.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`inline-block w-2 h-2 rounded-full ${
                          req.priority === 'critical' ? 'bg-red-500' :
                          req.priority === 'high' ? 'bg-orange-500' :
                          req.priority === 'medium' ? 'bg-blue-500' : 'bg-green-500'
                        }`} />
                        <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{req.priority}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">&middot;</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{req.status}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSchedule(req.id)}
                      disabled={schedulingId === req.id}
                      className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors disabled:opacity-50"
                    >
                      {schedulingId === req.id ? 'Scheduling...' : 'Schedule'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Dates Modal */}
      {editingTask && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setEditingTask(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-t-lg px-6 py-4">
              <h3 className="text-lg font-semibold text-white truncate">
                {editingTask.requirement_code ? `${editingTask.requirement_code} — ` : ''}{editingTask.title}
              </h3>
              <p className="text-sm text-blue-100 mt-0.5">Edit schedule dates</p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                <input
                  type="date"
                  value={editStartDate}
                  onChange={e => setEditStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Date</label>
                <input
                  type="date"
                  value={editDueDate}
                  onChange={e => setEditDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            {/* Dependencies */}
            {editingTaskDeps.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Dependencies</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {editingTaskDeps.map(dep => (
                    <div key={dep.depId} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                            dep.direction === 'Predecessor'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                          }`}>
                            {dep.direction}
                          </span>
                          {dep.otherCode && (
                            <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">{dep.otherCode}</span>
                          )}
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {dep.type.split('-').map((w: string) => w[0].toUpperCase()).join('')}
                          </span>
                        </div>
                        <p className="text-xs text-gray-800 dark:text-gray-200 truncate mt-0.5">{dep.otherTitle}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteDepFromModal(dep.depId, dep.requirementId)}
                        disabled={deletingDepId === dep.depId}
                        className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 flex-shrink-0"
                        title="Remove dependency"
                      >
                        {deletingDepId === dep.depId ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setEditingTask(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editStartDate || !editDueDate}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-md hover:from-indigo-700 hover:to-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Override SVAR Willow theme font to match app */}
      <style>{`
        .svar-gantt-wrapper .wx-willow-theme,
        .svar-gantt-wrapper .wx-willow-dark-theme {
          --wx-font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Arial', sans-serif;
          --wx-font-size: 13px;
          --wx-font-size-md: 14px;
          --wx-font-size-sm: 11px;
          --wx-font-size-hd: 14px;
        }
        .svar-gantt-wrapper .wx-willow-theme .wx-gantt,
        .svar-gantt-wrapper .wx-willow-dark-theme .wx-gantt {
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Arial', sans-serif;
          font-size: 13px;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .svar-gantt-wrapper .today-highlight {
          background: rgba(239, 68, 68, 0.08);
          border-left: 2px solid #ef4444;
        }
        .svar-gantt-wrapper [data-id="_spacer"] {
          color: transparent !important;
        }
        .svar-gantt-wrapper .wx-chart {
          scrollbar-width: thin;
          scrollbar-color: rgba(0,0,0,0.15) transparent;
        }
        .svar-gantt-wrapper .wx-chart::-webkit-scrollbar {
          height: 6px;
        }
        .svar-gantt-wrapper .wx-chart::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.15);
          border-radius: 3px;
        }
        .svar-gantt-wrapper .wx-chart::-webkit-scrollbar-track {
          background: transparent;
        }
        ${filteredScheduledReqs.map(req => {
          const idx = req.assigned_to_name ? assigneeColorMap.get(req.assigned_to_name) : undefined;
          if (idx === undefined) return '';
          const color = ASSIGNEE_COLORS[idx];
          return `
        [data-id="${req.id}"].wx-task {
          --wx-gantt-task-color: ${color.bg};
          --wx-gantt-task-fill-color: ${color.light};
          --wx-gantt-task-border-color: ${color.bg};
        }`;
        }).join('')}
        ${showCriticalPath ? `
        ${Array.from(criticalPathIds).map(id => `
        [data-id="${id}"].wx-task {
          --wx-gantt-task-color: #dc2626;
          --wx-gantt-task-fill-color: rgba(220, 38, 38, 0.25);
          --wx-gantt-task-border-color: #dc2626;
        }`).join('')}
        ${Array.from(criticalPathLinkIds).map(id => `
        .wx-link[data-link-id="${id}"] path { stroke: #dc2626; stroke-width: 2.5; }
        .wx-link[data-link-id="${id}"] polygon { fill: #dc2626; }`).join('')}
        ` : ''}
        ${selectedTaskId ? `
        ${Array.from(selectedLinkIds).map(id => `
        .wx-link[data-link-id="${id}"] path { stroke: #4f46e5; stroke-width: 2.5; opacity: 1; }
        .wx-link[data-link-id="${id}"] polygon { fill: #4f46e5; opacity: 1; }`).join('')}
        ` : ''}
      `}</style>
    </Layout>
  );
};

export default Timeline2;
